import type {
  BrandSpecial,
  BrandSpecialQualifyingStatus,
} from '@iheartjane/data-access';
import { getSpecialSlug } from '@iheartjane/menu';
import {
  Button,
  CheckIcon,
  ChevronRightIcon,
  Typography,
} from '@iheartjane/reefer';
import type { Nullable, Nullish } from '@iheartjane/types';
import { useMemo } from 'react';

import { BrandLogo } from '@jane/bloom/components/gold/brandLogo';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import type { BrandDiscount } from '@jane/bloom/viewModels/schemas/checkout';

import styles from './janeGoldSection.module.css';

interface JaneGoldCalloutProps {
  brand: {
    logo_url: Nullish<string>;
    name: Nullable<string>;
  };
  brandSpecial: BrandSpecial;
  cartBrandDiscounts: BrandDiscount[];
  isGoldUser: boolean;
  isMobile: boolean;
  isSegmentedDeal: boolean;
  isSignedIn: boolean;
  qualifyingStatus?: BrandSpecialQualifyingStatus;
}

export const JaneGoldCallout = ({
  brand,
  brandSpecial,
  cartBrandDiscounts,
  isGoldUser,
  isMobile,
  isSegmentedDeal,
  isSignedIn,
  qualifyingStatus,
}: JaneGoldCalloutProps) => {
  const generatePath = useFullDestination();
  const isBundle = brandSpecial?.special_type !== 'product';

  const isQualified =
    (isBundle && qualifyingStatus?.qualifying_status === 'qualified') ||
    !isBundle;

  // cart optimizer is the final source of truth as to which discount is actually applied
  // since it will find the best possible discount for that cart
  const isApplied = cartBrandDiscounts?.some(
    (discount) => discount.brand_special_id === brandSpecial.id
  );

  const isUnlocked = isApplied || isQualified;
  const showButton = isBundle && !isUnlocked && !isMobile;
  const showIconButton = isBundle && !isUnlocked && isMobile;
  const showSignIn = isSegmentedDeal && !isSignedIn;
  const showCheck = isApplied && (isSignedIn || !isSegmentedDeal);

  const linkPath = useMemo(
    () =>
      generatePath(
        `/jane-gold/${brandSpecial.id}/${getSpecialSlug(brandSpecial.title)}`
      ),
    [generatePath, brandSpecial.id, brandSpecial.title]
  );

  return (
    <div className={styles['janeGoldInfoBox']}>
      <div>
        <div className={styles['janelogoContainer']}>
          {brand.name && (
            <BrandLogo name={brand.name} logo_url={brand.logo_url} />
          )}
          <div className={styles['janeGoldText']}>
            {showSignIn && (
              <Typography color="grays-mid">Sign in for eligibility</Typography>
            )}
            <Typography variant="body-semibold">
              {brandSpecial?.title}
            </Typography>
          </div>
        </div>
      </div>
      {showButton && (
        <Button
          to={linkPath}
          ml="auto"
          label="View bundle"
          variant="tertiary"
        />
      )}

      {showIconButton && (
        <Button.Icon
          to={linkPath}
          ml="auto"
          variant="tertiary"
          icon={<ChevronRightIcon size="sm" />}
        />
      )}

      {/** @todo Once both Golden Gurls' and Bloom feature flags for bundles are on, we should be able to remove the "isGoldUser && !isBundle" condition */}
      <div>
        {(showCheck || (isGoldUser && !isBundle)) && (
          <CheckIcon data-testid="brand-discount-check-icon" />
        )}
      </div>
    </div>
  );
};
